import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Lock, Phone, Users, Cpu, MessageSquare, TrendingUp } from "lucide-react";
import flowAsset from "@/assets/jornada_vui_outbound_nda.svg.asset.json";

const CaseVoicebotsOdonto = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isEN = language === "en";

  const t = {
    back: isEN ? "Back to projects" : "Voltar aos projetos",
    nda: isEN ? "Under NDA" : "Sob NDA",
    title: isEN
      ? "Voicebot Ecosystem for a Dental Clinic (Under NDA)"
      : "Ecossistema de Voicebots para Clínica Odontológica (Sob NDA)",
    subtitle: isEN
      ? "UX Writer, Conversational Designer & Prompt Engineer — Telephony infrastructure via n8n + GPT-4o"
      : "UX Writer, Conversational Designer & Prompt Engineer — Infraestrutura de Telefonia via n8n + GPT-4o",
    tags: ["VUI", "Conversation Design", "Prompt Engineering", "GPT-4o", "n8n"],
    sections: isEN
      ? [
          {
            icon: Users,
            title: "1. The Business Challenge and Context",
            body: [
              "The client, a dental clinic focused on oral rehabilitation and high-value implants, needed to scale communication across multiple funnel stages — from cold lead reactivation to overdue payment recovery.",
              "The core audience is people over 50, predominantly from social classes B and C. This demographic carries critical particularities: strong aversion to telemarketing, anxiety around financial jargon, clinical fear of the procedure, and a heavy sense of shame tied to debt or tooth loss. The challenge was to automate calls without mechanizing care, ensuring an accessible, empathetic and resolutive auditory journey.",
            ],
          },
          {
            icon: Cpu,
            title: "2. VUI Strategy and Modular Architecture",
            body: [
              "To ensure consistency and scalability, a Modular Design approach was adopted, creating an architectural framework (Base Skeleton) that serves as foundation for any agent in the ecosystem. This infrastructure separates the LLM's cognitive load from business rules managed by the platform:",
            ],
            bullets: [
              "Strict Turn-Taking and Accessibility: The 50+ audience processes complex information at its own pace. Flows were designed with micro-turns and strict interruption rules immediately after question marks, ensuring the AI actively listens and doesn't talk over the patient.",
              "Infrastructure Delegation: Silence handling (NO INPUT) and speech recognition failures (ASR ERROR) were removed from the prompt and delegated to the telephony runtime. This reduced token consumption and shielded the agents from connection issues.",
              "Dynamic Variable Injection: The system consumes the clinic's scheduling API in milliseconds, injecting time variables precisely to avoid hallucinations during appointment booking.",
            ],
          },
          {
            icon: MessageSquare,
            title: "3. The Persona Ecosystem",
            body: [
              "With the infrastructure standardized, three distinct AI personas were developed, orchestrated with specific guidelines from Neurolinguistic Programming (NLP) and Choice Architecture:",
            ],
            bullets: [
              "Follow-up Agent: Focused on recovering recent quotes. The script uses contextualized social-proof pills, triggering anonymized success stories from other patients only when price objections or clinical fear are detected.",
              "Financial Consultant: Designed for friendly collections. Applies the \"Blind System\" technique to externalize blame for automated billing and uses strictly domestic vocabulary, removing \"banking-speak\" to welcome the patient's shame and reduce friction.",
              "Reactivation Specialist: Focused on dormant leads from the cold base. The prompt deflects from price and applies emotional anchoring, reminding the patient of the daily impact of tooth loss on their routine, plus risk reversal to overcome distance objections.",
            ],
          },
          {
            icon: Cpu,
            title: "4. Prompt Engineering and De-escalation",
            body: [
              "Prompt optimization required meticulous work to tame GPT-4o's natural verbosity and guarantee low latency in the n8n architecture:",
            ],
            bullets: [
              "Peak-End Rule: Rigorous structuring of negative outputs. If the patient refuses treatment or negotiation, the prompt instructs the AI to end the call quickly, with empathy and no sales pressure, keeping the door open for the future.",
              "Conditional Linearization: Replacing abstract Go-To commands with literal responses and paths inside the prompt itself, reducing the model's logical processing effort and delivering an ideal Time To First Token (TTFT) for real-time voice conversations.",
            ],
          },
          {
            icon: TrendingUp,
            title: "5. Impact and Results",
            body: [
              "The ecosystem transformed the clinic's phone from a friction channel into a tool of mass care and high conversion. Architectural standardization increased bookings on active campaigns and generated a scalable base: the network can now launch new voice agents for different operational sectors by reusing the same logical and secure Voice User Interface structure.",
            ],
          },
        ]
      : [
          {
            icon: Users,
            title: "1. O Desafio de Negócio e o Cenário",
            body: [
              "O cliente, uma clínica odontológica focada em reabilitação oral e implantes de alto valor agregado, precisava escalar sua capacidade de comunicação em diferentes etapas do funil — desde a reativação de leads frios até a cobrança de inadimplentes.",
              "O público-alvo central é composto por pessoas acima de 50 anos, predominantemente das classes B e C. Essa demografia apresenta particularidades críticas: alta aversão a telemarketing, ansiedade em relação a jargões financeiros, medo clínico do procedimento e forte senso de vergonha atrelado a dívidas ou perda dentária. O desafio era automatizar as ligações sem mecanizar o acolhimento, garantindo uma jornada auditiva acessível, empática e resolutiva.",
            ],
          },
          {
            icon: Cpu,
            title: "2. Estratégia de VUI e Arquitetura Modular",
            body: [
              "Para garantir consistência e escalabilidade, foi adotada uma abordagem de Modular Design, criando um framework arquitetural (Esqueleto Base) que serve de fundação para qualquer agente do ecossistema. Essa infraestrutura separa a carga cognitiva do LLM das regras de negócio gerenciadas pela plataforma:",
            ],
            bullets: [
              "Turn-Taking Rígido e Acessibilidade: O público 50+ processa informações complexas em um ritmo próprio. Os fluxos foram desenhados com micro-turnos e regras estritas de interrupção imediata após pontos de interrogação, garantindo que a IA escute ativamente e não atropele o paciente.",
              "Delegação de Infraestrutura: Tratamentos de silêncio (NO INPUT) e falhas de reconhecimento de voz (ASR ERROR) foram retirados do prompt e delegados ao runtime do sistema de telefonia. Isso reduziu o consumo de tokens e blindou os agentes contra problemas de conexão.",
              "Injeção Dinâmica de Variáveis: O sistema consome a API da agenda da clínica em milissegundos, injetando variáveis de horário de forma precisa para evitar alucinações durante as marcações de consultas.",
            ],
          },
          {
            icon: MessageSquare,
            title: "3. O Ecossistema de Personas",
            body: [
              "Com a infraestrutura padronizada, foram desenvolvidas três personas de IA distintas, orquestradas com diretrizes específicas de Programação Neurolinguística (PNL) e Arquitetura de Escolhas:",
            ],
            bullets: [
              "Agente de Follow-up: Focada no resgate de orçamentos recentes. O roteiro utiliza pílulas de prova social contextualizadas, acionando histórias anonimizadas de sucesso de outros pacientes apenas quando são detectadas objeções de preço ou medo clínico.",
              "Consultora Financeira: Projetada para cobranças amigáveis. Aplica a técnica do \"Sistema Cego\" para externalizar a culpa da cobrança automática e utiliza vocabulário estritamente doméstico, removendo o \"bancariês\" para acolher a vergonha do paciente e reduzir o atrito.",
              "Especialista em Reativação: Focado em leads adormecidos da base fria. O prompt desvia o foco do preço e aplica ancoragem emocional, relembrando o paciente do impacto diário da perda dentária na sua rotina, além de utilizar reversão de risco para contornar objeções de distância.",
            ],
          },
          {
            icon: Cpu,
            title: "4. Engenharia de Prompt e Desescalada",
            body: [
              "A otimização dos prompts exigiu um trabalho minucioso para domar a verbosidade natural do GPT-4o e garantir baixa latência na arquitetura n8n:",
            ],
            bullets: [
              "Peak-End Rule (Regra do Pico-Fim): Estruturação rigorosa de saídas negativas. Se o paciente recusa o tratamento ou a negociação, o prompt instrui a IA a encerrar a chamada rapidamente, com empatia e sem pressão de vendas, mantendo a porta aberta para o futuro.",
              "Linearização de Condicionais: Substituição de comandos abstratos de Go-To por respostas e caminhos literais dentro do próprio prompt, reduzindo o esforço de processamento lógico do modelo e entregando um Time To First Token (TTFT) ideal para conversas por voz em tempo real.",
            ],
          },
          {
            icon: TrendingUp,
            title: "5. Impacto e Resultados",
            body: [
              "O ecossistema transformou o telefone da clínica de um canal de atrito em uma ferramenta de acolhimento em massa e alta conversão. A padronização arquitetural aumentou o agendamento nas campanhas ativas e gerou uma base escalável: a rede agora pode lançar novos agentes de voz para diferentes setores operacionais reaproveitando a mesma estrutura lógica e segura de Voice User Interface.",
            ],
          },
        ],
    flowTitle: isEN ? "VUI Journey Flow — Post-evaluation Outbound Reactivation" : "Fluxo de jornada VUI — Outbound de reativação pós-avaliação",
    flowCaption: isEN
      ? "Illustrative diagram of the conversational flow (content under NDA)."
      : "Diagrama ilustrativo do fluxo conversacional (conteúdo sob NDA).",
  };

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-background">
        <section className="pt-32 pb-12 gradient-hero">
          <div className="max-w-3xl mx-auto px-6">
            <Button onClick={() => navigate("/")} variant="ghost" className="mb-8 hover:bg-primary/10">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t.back}
            </Button>

            <div className="flex flex-wrap gap-2 mb-6">
              <Badge className="bg-primary text-primary-foreground">
                <Lock className="h-3 w-3 mr-1" />
                {t.nda}
              </Badge>
              {t.tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="bg-primary/10 text-primary">
                  {tag}
                </Badge>
              ))}
            </div>

            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">{t.title}</h1>
            <p className="text-lg text-muted-foreground flex items-start gap-2">
              <Phone className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
              <span>{t.subtitle}</span>
            </p>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-3xl mx-auto px-6 space-y-10">
            {t.sections.map((section, idx) => {
              const Icon = section.icon;
              return (
                <Card key={idx} className="p-8 gradient-premium border-primary/20">
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                    <Icon className="h-6 w-6 text-primary" />
                    {section.title}
                  </h2>
                  <div className="space-y-4 text-muted-foreground leading-relaxed">
                    {section.body.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                    {section.bullets && (
                      <ul className="space-y-3 pl-4 border-l-2 border-primary/40">
                        {section.bullets.map((b, i) => (
                          <li key={i} className="text-muted-foreground">{b}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Card>
              );
            })}

            <Card className="p-6 gradient-premium border-primary/20">
              <h3 className="text-xl font-bold mb-4">{t.flowTitle}</h3>
              <div className="rounded-lg overflow-hidden bg-white p-4">
                <img src={flowAsset.url} alt={t.flowTitle} className="w-full h-auto" />
              </div>
              <p className="text-sm text-muted-foreground mt-3 italic">{t.flowCaption}</p>
            </Card>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default CaseVoicebotsOdonto;
